import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { visitorsApi, type Dimension } from '../../api/visitors'
import { fmtInt, PUBLICATION_NAMES } from '../../lib/format'
import { rate } from '../../lib/visitorsFormat'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { QueryProblem, Skeleton, VisitorsFrame, linkCls, tdn, th, thr } from './VisitorsFrame'

const DIMS: { key: Dimension; label: string }[] = [
  { key: 'channel', label: 'Channel' },
  { key: 'device', label: 'Device' },
  { key: 'country', label: 'Country' },
  { key: 'landing', label: 'Landing page' },
  { key: 'campaign', label: 'Campaign' },
]

export function VisitorsBreakdown() {
  const { pub = '', dimension = '' } = useParams()
  const { range, search } = useVisitorsRange()
  const okPub = Object.hasOwn(PUBLICATION_NAMES, pub)
  const dim = DIMS.find((d) => d.key === dimension)
  const q = useQuery({
    queryKey: ['visitors-breakdown', pub, range.from, range.to, dimension],
    queryFn: () => visitorsApi.breakdown(pub, range, dimension as Dimension),
    enabled: okPub && !!dim,
  })
  const base = `/visitors/${pub}`
  const label = dim?.label ?? dimension

  return (
    <VisitorsFrame
      pub={pub}
      valid={okPub}
      title={`By ${label.toLowerCase()}`}
      crumbs={[{ label: 'Breakdown' }, { label }]}
    >
      {!dim ? (
        <Panel accent="red">
          <p className="text-paper">Unknown breakdown</p>
          <Link to={`${base}?${search}`} className={`${linkCls} font-mono text-[12px]`}>Back to the title page</Link>
        </Panel>
      ) : (
        <>
          <nav aria-label="Breakdown dimension" className="flex flex-wrap gap-2">
            {DIMS.map((d) => (
              <Link
                key={d.key}
                to={`${base}/breakdown/${d.key}?${search}`}
                aria-current={d.key === dim.key ? 'page' : undefined}
                className={`min-h-[32px] px-3 py-2 font-mono text-[12px] border ${
                  d.key === dim.key ? 'border-amber-glow text-amber-glow bg-ink-700' : 'border-ink-500 text-paper-dim hover:text-paper hover:bg-ink-700'
                }`}
              >
                {d.label}
              </Link>
            ))}
          </nav>
          {q.isError ? (
            <QueryProblem error={q.error} pub={pub} />
          ) : !q.data ? (
            <Skeleton />
          ) : (
            <Panel label={`Funnel by ${label.toLowerCase()}`} accent="amber">
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-[12px] text-paper">
                  <thead>
                    <tr className="border-b border-ink-500">
                      <th className={th}>{label}</th>
                      {['Visitors', 'Visits', 'Engaged', 'Considered', 'Checkout', 'Paid', 'Visit → paid'].map((h) => <th key={h} className={thr}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.rows.map((r) => (
                      <tr key={r.key} className="border-b border-ink-500/50">
                        <td className="px-3 py-2">
                          <Link to={`${base}/sessions?${dim.key}=${encodeURIComponent(r.key)}&${search}`} className={linkCls}>{r.key}</Link>
                        </td>
                        <td className={tdn}>{fmtInt(r.visitors)}</td>
                        <td className={tdn}>{fmtInt(r.sessions)}</td>
                        <td className={tdn}>{fmtInt(r.engaged)}</td>
                        <td className={tdn}>{fmtInt(r.considered)}</td>
                        <td className={tdn}>{fmtInt(r.checkout)}</td>
                        <td className={tdn}>{fmtInt(r.paid)}</td>
                        <td className={tdn}>{rate(r.conversion)}</td>
                      </tr>
                    ))}
                    {q.data.rows.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-paper-faint">No visits in this range</td></tr>}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
        </>
      )}
    </VisitorsFrame>
  )
}
